// -----------------------------------------------------------------------------
// InfoPanel
//
// Small type-specific informational panel used instead of the bare ComingSoonStub
// for item types that aren't a wired-up action in this surface:
//
//   LINKED_CHECKLIST   — tracks a separate linked checklist; offers a "Mark
//                        complete" button that POSTs /complete/ then onComplete.
//   START_TREATMENT    — stage-mismatched in a started-treatment hub; info + Close.
//   COMPLETE_TREATMENT — info pointing at the "Complete treatment" footer; Close.
//
// LINKED_CHECKLIST mark-complete mirrors LinkedChecklistCell (which uses
// useStatusApi.postState → /complete/) — it does the POST itself then calls
// onComplete(item) as a refresh signal.
//
//   POST /api/v1/practices/{practiceId}/tasks/{taskId}/checklist/{itemId}/complete/
//   body { completion_state: 'YES' }
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { callApi } from 'src/utils/apiCall';
import Iconify from 'src/components/Iconify';

const INFO_META = {
    LINKED_CHECKLIST: {
        icon: 'eva:link-2-outline',
        color: '#078DEE',
        bg: 'rgba(7,141,238,0.10)',
        title: 'Linked checklist',
        body: 'This tracks a linked checklist — complete its items to finish. You can also mark it complete here once that checklist is done.',
        canComplete: true,
    },
    START_TREATMENT: {
        icon: 'solar:heart-pulse-bold-duotone',
        color: '#FFAB00',
        bg: 'rgba(255,171,0,0.12)',
        title: 'Start treatment',
        body: 'This step starts the treatment. It is handled from the lead’s status actions, not from here.',
        canComplete: false,
    },
    COMPLETE_TREATMENT: {
        icon: 'solar:verified-check-bold-duotone',
        color: '#00A76F',
        bg: 'rgba(0,167,111,0.10)',
        title: 'Complete treatment',
        body: 'Use the Complete treatment button below to finish and close this treatment.',
        canComplete: false,
    },
};

const DEFAULT_META = {
    icon: 'mdi:information-outline',
    color: '#6e6e73',
    bg: 'rgba(110,110,115,0.10)',
    title: 'Action',
    body: 'This step is tracked here. Mark it complete from the checklist when done.',
    canComplete: false,
};

export default function InfoPanel({ item, practiceId, onComplete, onClose }) {
    const [submitting, setSubmitting] = useState(false);
    const meta = INFO_META[item?.item_type] || DEFAULT_META;

    const taskId = item?.taskId ?? item?.rawTask?.id;
    const itemId = item?.itemId ?? item?.id;

    const handleComplete = async () => {
        if (submitting) return;
        if (!practiceId || !taskId || !itemId) {
            onComplete?.(item);
            return;
        }
        setSubmitting(true);
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/complete/`,
                'POST',
                { completion_state: 'YES' }
            );
            if (res.ok) {
                onComplete?.(item);
            } else {
                dispatch(updateError({ success: false, message: res?.data?.detail || 'Failed to mark complete — please try again.' }));
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err?.message || 'Failed to mark complete — please try again.' }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box
            data-testid="info-panel"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                px: 3,
                py: 2.5,
                gap: 1.75,
                textAlign: 'center',
            }}
        >
            <Box
                sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '14px',
                    bgcolor: meta.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Iconify icon={meta.icon} width={26} height={26} sx={{ color: meta.color }} />
            </Box>

            <Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em' }}>
                    {item?.title || meta.title}
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#6e6e73', mt: 0.5, px: 1, lineHeight: 1.5 }}>
                    {meta.body}
                </Typography>
            </Box>

            {meta.canComplete && (
                <Button
                    data-testid="info-panel-complete"
                    variant="contained"
                    disableElevation
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Iconify icon="mdi:check-circle-outline" width={16} height={16} />}
                    onClick={handleComplete}
                    sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, bgcolor: meta.color, '&:hover': { filter: 'brightness(0.92)' }, boxShadow: 'none' }}
                >
                    Mark complete
                </Button>
            )}

            <Button
                variant={meta.canComplete ? 'text' : 'outlined'}
                size="small"
                disabled={submitting}
                onClick={onClose}
                sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '13px', color: meta.canComplete ? '#6e6e73' : undefined }}
            >
                Close
            </Button>
        </Box>
    );
}

InfoPanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onComplete: PropTypes.func,
    onClose: PropTypes.func.isRequired,
};

InfoPanel.defaultProps = {
    item: null,
    practiceId: null,
    onComplete: null,
};
